import { Button, Icon, Menu, MenuItem, Popover } from "@snpjs/core";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "../confirmDialog/ConfirmDialog";
import { useConfig } from "../../hooks/useConfig";
import { Message } from "../../services/Message";
import { texts } from "../../i18n/texts";
import useTranslation from "../../hooks/useTranslation";
import { readLocalStorage } from "../../utils/readLocalStorage";
import { writeLocalStorage } from "../../utils/writeLocalStorage";
import styles from "./AnnouncementBanner.module.scss";

const ANNOUNCEMENT_DISMISSED_KEY = "swpo.announcement.dismissed";
const ANNOUNCEMENT_SESSION_HIDDEN_KEY = "swpo.announcement.hidden";

/**
 * Converts the limited markup supported by feature announcements into React
 * elements. Keeping the allowlist explicit avoids injecting backend content
 * into the DOM as raw HTML.
 */
const renderAnnouncementText = (text: string) => {
  let isBold = false;

  return text.split(/(<\/?b\s*>|<br\s*\/?>)/gi).map((part, index) => {
    const tag = part.toLowerCase();

    if (/^<b\s*>$/.test(tag)) {
      isBold = true;
      return null;
    }

    if (/^<\/b\s*>$/.test(tag)) {
      isBold = false;
      return null;
    }

    if (/^<br\s*\/?>$/.test(tag)) {
      return <br key={index} />;
    }

    return isBold ? <strong key={index}>{part}</strong> : part;
  });
};

export const AnnouncementBanner: React.FC = () => {
  const config = useConfig();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const announcementText = config.SWPO_FEATURE_INFO?.trim();
  const announcementLink = config.NEW_FEATURE_LINK?.trim();
  const isExpandable = Boolean(announcementText && announcementText.length > 160);

  useEffect(() => {
    if (!announcementText) {
      setIsVisible(false);
      return;
    }

    const isDismissed = readLocalStorage<boolean>(ANNOUNCEMENT_DISMISSED_KEY);
    const isHiddenForSession = sessionStorage.getItem(
      ANNOUNCEMENT_SESSION_HIDDEN_KEY
    );

    setIsVisible(
      !isDismissed && isHiddenForSession !== "true" && announcementText.length > 0
    );
  }, [announcementText]);

  useEffect(() => {
    setIsExpanded(false);
  }, [announcementText]);

  const hideForSession = () => {
    sessionStorage.setItem(ANNOUNCEMENT_SESSION_HIDDEN_KEY, "true");
    setIsVisible(false);
  };

  const confirmDismiss = () => {
    setShowConfirm(true);
  };

  const dismissPermanently = () => {
    writeLocalStorage<boolean>(ANNOUNCEMENT_DISMISSED_KEY, true);
    setIsVisible(false);
    setShowConfirm(false);
    Message.warning(t(texts.announcementBanner.dismissedToast));
  };

  if (!isVisible || !announcementText) {
    return null;
  }

  const menu = (
    <Menu>
      <MenuItem
        text={t(texts.announcementBanner.close)}
        onClick={hideForSession}
      />
      <MenuItem
        text={t(texts.announcementBanner.closeAndDismiss)}
        intent="danger"
        onClick={confirmDismiss}
      />
    </Menu>
  );

  return (
    <>
      <div className={styles.banner}>
        <div className={styles.leading}>
          <div className={styles.iconWrapper}>
            <Icon icon="info" />
          </div>
          <div className={styles.textArea}>
            <span className={styles.heading}>
              {t(texts.announcementBanner.heading)}
            </span>
            <div className={styles.textGroup}>
              <span
                className={`${styles.text} ${isExpanded ? styles.expanded : styles.collapsed}`}
              >
                {renderAnnouncementText(announcementText)}
              </span>
              {isExpandable ? (
                <Button
                  minimal={true}
                  className={styles.toggleButton}
                  onClick={() => setIsExpanded((current) => !current)}
                >
                  {isExpanded
                    ? t(texts.announcementBanner.showLess)
                    : t(texts.announcementBanner.showMore)}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.trailing}>
          {announcementLink ? (
            <a
              href={announcementLink}
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(texts.announcementBanner.viewDetails)}
              <Icon icon="arrow-right" />
            </a>
          ) : null}
          <Popover content={menu} placement="bottom-end">
            <Button
              minimal={true}
              icon={<Icon icon="x" />}
              aria-label={t(texts.announcementBanner.closeButtonLabel)}
            />
          </Popover>
        </div>
      </div>
      <ConfirmDialog
        isOpen={showConfirm}
        title={t(texts.announcementBanner.dismissDialogTitle)}
        text={t(texts.announcementBanner.dismissDialogText)}
        onOkay={dismissPermanently}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
};
